import { Formik, Form } from 'formik';
import { useSelector } from 'react-redux';
import React, { Fragment, useState } from 'react';

import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Grid, Button, FormHelperText } from '@mui/material';

import { isLoading } from 'hooks/requests';
import { userFormStyles } from './style/style';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { useCompanyService } from 'store/services/company.service';
import { usersSchema, usersInitialValues } from './utils/usersSchema';
import { Modal, TextField, Typography, SnackBarAlert, PhoneNumber } from 'shared';

const UserForm = ({ open, type, initialValues, handleCloseModal, handleUserFormSuccess }) => {
    const classes = userFormStyles();
    const companyData = useSelector((state) => state.company.data);
    const { createCompanyUser, updateCompanyUser } = useCompanyService();

    const [alertData, setAlertData] = useState({
        open: false,
        message: '',
        severity: '',
    });

    const handleSubmit = async (values) => {
        try {
            const data = { company_id: companyData?.id, ...values };
            let response = null;
            if (type === 'Add') {
                response = await createCompanyUser(data);
            } else {
                delete data.email;
                data.id = initialValues?.id;
                response = await updateCompanyUser(data);
            }
            handleUserFormSuccess(response.message);
        } catch (error) {
            handleErrorAlert(setAlertData, error?.response?.data?.error);
        }
    };

    return (
        <Fragment>
            <Modal
                fullWidth
                open={open}
                title={`${type} User`}
                actionBtnTitle={<Typography text={type === 'Add' ? 'Create' : 'Update'} />}
                handleClose={handleCloseModal}
            >
                <Formik
                    initialValues={usersInitialValues(initialValues)}
                    validationSchema={usersSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue, errors, touched, setTouched }) => {
                        return (
                            <Form>
                                <Box width="100%" gap={2} mt={1} border="none">
                                    <Grid container alignItems="center" spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                name="firstName"
                                                placeholder="First Name"
                                                inlineLabel="First Name"
                                                value={values.firstName}
                                                onChange={(e) =>
                                                    setFieldValue('firstName', e.target.value)
                                                }
                                                customLabelStyles={classes.customLabel}
                                                error={touched.firstName && !!errors.firstName}
                                                helperText={touched.firstName && errors.firstName}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                name="lastName"
                                                placeholder="Last Name"
                                                inlineLabel="Last Name"
                                                value={values.lastName}
                                                onChange={(e) =>
                                                    setFieldValue('lastName', e.target.value)
                                                }
                                                customLabelStyles={classes.customLabel}
                                                error={touched.lastName && !!errors.lastName}
                                                helperText={touched.lastName && errors.lastName}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                name="email"
                                                placeholder="Email"
                                                inlineLabel="Email"
                                                value={values.email}
                                                onChange={(e) =>
                                                    setFieldValue('email', e.target.value)
                                                }
                                                disabled={type != 'Add'}
                                                customLabelStyles={classes.customLabel}
                                                error={touched.email && !!errors.email}
                                                helperText={touched.email && errors.email}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <Box display="flex" flexDirection="column">
                                                <PhoneNumber
                                                    fullWidth
                                                    name="phoneNumber"
                                                    placeholder="Enter Phone Number"
                                                    value={values.phoneNumber}
                                                    onChange={(e) => {
                                                        setFieldValue(
                                                            'phoneNumber',
                                                            e.target.value,
                                                        );
                                                        setTouched({
                                                            ...touched,
                                                            phoneNumber: false,
                                                        });
                                                    }}
                                                />

                                                {touched.phoneNumber && errors.phoneNumber && (
                                                    <FormHelperText
                                                        error
                                                        sx={{
                                                            fontSize: '13px',
                                                            fontWeight: 600,
                                                            mt: 1,
                                                            textAlign: 'left',
                                                        }}
                                                    >
                                                        {errors.phoneNumber}
                                                    </FormHelperText>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        marginTop: 3,
                                    }}
                                >
                                    <Button sx={classes.cancelButton} onClick={handleCloseModal}>
                                        Cancel
                                    </Button>
                                    <LoadingButton
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        loading={isLoading()}
                                    >
                                        {type === 'Add' ? 'Create' : 'Update'}
                                    </LoadingButton>
                                </Box>
                            </Form>
                        );
                    }}
                </Formik>
            </Modal>

            <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
        </Fragment>
    );
};

export default UserForm;
